import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeFormParams, resetForm, toggleCampaignDrawerOpen } from 'store/dealManagement/reducer';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { getHasDirectSalesCampaignInfoChanged } from 'store/dealManagement/selectors/getHasDirectSalesCampaignInfoChanged';
import { getIsDirectSalesCampaignInfoSaveDisabled } from 'store/dealManagement/selectors/getIsDirectSalesCampaignInfoSaveDisabled';
import { Store } from 'components/common/types/Store.types';
import useUpdateDirectSalesCampaign from 'components/pages/Planner/hooks/DirectSalesPlannerActions/useUpdateDirectSalesCampaign';
import { useHandleCampaignFieldChanges } from 'components/pages/Planner/hooks/DirectSalesPlannerActions/useHandleCampaignFieldChanges';

interface UseCloseCampaignDrawer {
  isUnsavedChangesModalOpen: boolean;
  handleCloseDrawer: () => void;
  handleDismissModal: () => void;
  handleDiscard: () => void;
  handleSaveAndClose: () => Promise<void>;
  isSaveDisabled: boolean;
}

export const useCloseCampaignDrawer = (): UseCloseCampaignDrawer => {
  const dispatch = useDispatch();
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const hasUnsavedChanges = useSelector(getHasDirectSalesCampaignInfoChanged);
  const isCampaignDrawerOpen = useSelector((state: Store) => state.dealManagement.isCampaignDrawerOpen);
  const isSaveDisabled = useSelector(getIsDirectSalesCampaignInfoSaveDisabled);
  const { saveDeal } = useUpdateDirectSalesCampaign();
  const { captureChangesBeforeSave, handleAfterSave } = useHandleCampaignFieldChanges();

  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  const handleCloseDrawer = useCallback((): void => {
    if (!isCampaignDrawerOpen) {
      dispatch(toggleCampaignDrawerOpen(true));
      return;
    }

    if (isDirectSalesCampaignType && hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }

    dispatch(toggleCampaignDrawerOpen(false));
  }, [dispatch, isCampaignDrawerOpen, isDirectSalesCampaignType, hasUnsavedChanges]);

  const handleDismissModal = useCallback((): void => {
    setIsUnsavedChangesModalOpen(false);
  }, []);

  const handleDiscard = useCallback((): void => {
    setIsUnsavedChangesModalOpen(false);
    dispatch(resetForm());
    dispatch(toggleCampaignDrawerOpen(false));
  }, [dispatch]);

  const handleSaveAndClose = useCallback(async (): Promise<void> => {
    setIsUnsavedChangesModalOpen(false);
    captureChangesBeforeSave();

    const dealSavedSuccessfully = await saveDeal();

    if (dealSavedSuccessfully) {
      dispatch(changeFormParams({ isCampaignDrawerOpen: false }));
      await handleAfterSave();
    }
  }, [dispatch, saveDeal, captureChangesBeforeSave, handleAfterSave]);

  return {
    isUnsavedChangesModalOpen,
    handleCloseDrawer,
    handleDismissModal,
    handleDiscard,
    handleSaveAndClose,
    isSaveDisabled,
  };
};
